import imageCompression from "browser-image-compression";
import { fetchWithRetry } from "../utils/retry.util";

interface Dimension {
  width: number;
  height: number;
}

export class ImageService {
  private static readonly MAX_SIZE = 500 * 1024; // 500KB in bytes
  private static readonly MAX_DIMENSION = 3072;
  private static readonly MIN_DIMENSION = 512;
  private static readonly MAX_RESOLUTION = 4096;

  static async compressImageIfNeeded(img: File): Promise<File> {
    const dimension = await this.getDimension(img);

    const isImgValid =
      img.size <= this.MAX_SIZE &&
      dimension.width <= this.MAX_DIMENSION &&
      dimension.height <= this.MAX_DIMENSION;

    if (isImgValid) {
      return img;
    }

    const options = {
      maxSizeMB: 0.5,
      maxWidthOrHeight: this.MAX_DIMENSION,
      useWebWorker: true,
      fileType: img.type,
    };

    return await imageCompression(img, options);
  }

  static async validateIconImageResolution(
    img: File
  ): Promise<{ isValid: boolean; error?: string }> {
    try {
      const dimension = await this.getDimension(img);

      // Check minimum resolution
      if (
        dimension.width < this.MIN_DIMENSION ||
        dimension.height < this.MIN_DIMENSION
      ) {
        return {
          isValid: false,
          error: `Image resolution must be at least ${this.MIN_DIMENSION}x${this.MIN_DIMENSION} pixels. Current: ${dimension.width}x${dimension.height}`,
        };
      }

      // Check maximum resolution
      if (
        dimension.width > this.MAX_RESOLUTION ||
        dimension.height > this.MAX_RESOLUTION
      ) {
        return {
          isValid: false,
          error: `Image resolution must not exceed ${this.MAX_RESOLUTION}x${this.MAX_RESOLUTION} pixels. Current: ${dimension.width}x${dimension.height}`,
        };
      }

      return { isValid: true };
    } catch (error) {
      return {
        isValid: false,
        error: "Failed to validate image resolution",
      };
    }
  }

  static async convertImageToBase64(img: File): Promise<string> {
    return await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result as string;
        const base64 = base64String.split(",")[1];
        resolve(base64);
      };
      reader.readAsDataURL(img);
    });
  }

  static base64ToFile = (
    base64Content: string,
    filename = "image.png",
    mimeType = "image/png"
  ): File => {
    // Convert base64 to binary
    const byteString = atob(base64Content);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);

    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }

    // Create Blob and File
    const blob = new Blob([ab], { type: mimeType });
    return new File([blob], filename, { type: mimeType });
  };

  static async fetchImageAsFile(
    url: string,
    filename: string,
    fallbackMimeType: string
  ): Promise<File> {
    if (import.meta.env.DEV) {
      url = url.replace("https://dev.darvin.dev", "");
    }
    const response = await fetchWithRetry(url);
    const blob = await response.blob();
    // Use MIME type from response, fallback to provided mimeType if not available
    const mimeType = blob.type || fallbackMimeType;
    return new File([blob], filename, { type: mimeType });
  }

  private static getDimension(imgFile: File): Promise<Dimension> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };
      img.onerror = () => {
        reject(new Error("Failed to load image"));
      };
      img.src = URL.createObjectURL(imgFile);
    });
  }
}
